/**
 * **Where a kingdom's name belongs on the map.**
 *
 * The obvious answer — the centroid, `sumX/count` — is wrong in the one case
 * that matters most, and wrong in the way that looks like a bug rather than an
 * approximation. A realm shaped like a ring puts its centroid in the **hole**:
 * the label floats over ground the kingdom does not own, usually somebody
 * else's, and the bigger and more interesting the empire the more likely it is
 * to have swallowed a neighbour and left one. A realm split across two
 * continents is worse — the centroid lands in the sea between them.
 *
 * So the name goes on the **largest contiguous mass of ground the realm
 * actually holds**, found like this:
 *
 *   1. Bucket the whole map into `CELL`-sized squares once and count, per
 *      kingdom, how many tiles it owns in each. One pass over the map.
 *   2. Treat the cells it appears in as a graph and find the **connected
 *      components**. The biggest by tile count is its heartland; a ring's hole
 *      is in no component at all, and an overseas holding is in a different one.
 *   3. Take that component's weighted centre, and refine to the centroid of the
 *      real tiles around it — so the label sits in the middle of the mass
 *      rather than in the middle of one arbitrary square.
 *
 * **Why not simply the densest cell.** That was the first attempt and it was
 * wrong in a way worth recording: a cell *saturates*. Once a kingdom owns all
 * 144 tiles of one, holding more land nearby does not make it denser — so a
 * sprawling empire and the four-cell patch it started in tie at 144, and the
 * winner is decided by whichever the scan reaches first. Names sat on players'
 * starting corners for exactly that reason. Size has to be measured over the
 * whole connected mass, not at its best single point.
 *
 * **Why a grid and not a real connected-components pass.** Flood-filling 282 240
 * tiles per kingdom, for up to 130 kingdoms, every time the border moves, is not
 * a thing a 60 fps client can do. This is one linear sweep of the map, no
 * allocation per kingdom, and it is *right* about the case it exists for: the
 * hole in a donut is empty, and empty cells lose.
 *
 * It runs on a timer rather than per frame — a name does not need to chase the
 * border at 60 Hz — and what it produces is a *target*, which the label glides
 * toward. That last part is not cosmetic: a label recomputed in jumps and
 * painted without smoothing is exactly what a flickering label looks like.
 */

import { MAP_H, MAP_W } from '../shared/constants';
import { state } from './state';

/**
 * Tiles per cell edge. Twelve is a compromise found by what it has to separate:
 * fine enough that the hole in a realistic donut is several cells across and
 * cannot be bridged, coarse enough that the histogram stays about two thousand
 * entries per kingdom instead of a quarter of a million.
 */
const CELL = 12;
const CW = Math.ceil(MAP_W / CELL);
const CH = Math.ceil(MAP_H / CELL);
const NCELLS = CW * CH;

/** How often the heavy sweep runs. A name does not need to chase a border. */
export const ANCHOR_REFRESH_MS = 700;

/** How fast a label slides to a new home: fraction of the gap per second. */
const GLIDE_PER_SECOND = 6;

export interface Anchor {
  /** where the label is now, in tiles — what the renderer reads */
  x: number;
  y: number;
  /** where it is heading, in tiles — what the sweep writes */
  tx: number;
  ty: number;
  /** tiles in the blob it settled on, so a caller can tell a rump from an empire */
  blob: number;
}

const anchors = new Map<number, Anchor>();

/** Reused between sweeps; grown, never shrunk, and zeroed each time. */
let hist = new Int32Array(0);
let slots = 0;

/** Flood-fill scratch. `seen` is stamped, never cleared. */
const seen = new Int32Array(NCELLS);
const stack = new Int32Array(NCELLS);
let stamp = 0;

export function anchorFor(id: number): Anchor | undefined {
  return anchors.get(id);
}

/**
 * The heavy sweep. One pass over the map, then one pass over the histogram.
 *
 * Everything here is deliberately allocation-free in the hot loop: the
 * histogram is reused, the slot table is a plain array indexed by the same
 * order `state.players` iterates, and nothing per-tile touches a Map.
 */
export function refreshAnchors() {
  const live = [...state.players.values()].filter(p => p.count > 0);
  if (live.length === 0) { anchors.clear(); return; }

  /* --- slot table: player id -> dense index --- */
  const slotOf = new Map<number, number>();
  for (let i = 0; i < live.length; i++) slotOf.set(live[i].id, i);

  if (live.length > slots) {
    slots = live.length + 16;          // headroom, so joining does not realloc
    hist = new Int32Array(slots * NCELLS);
  }
  hist.fill(0, 0, live.length * NCELLS);

  /* --- one pass over the map --- */
  const owner = state.owner;
  for (let y = 0; y < MAP_H; y++) {
    const cellRow = ((y / CELL) | 0) * CW;
    const tileRow = y * MAP_W;
    for (let x = 0; x < MAP_W; x++) {
      const id = owner[tileRow + x];
      if (id === 0) continue;
      const slot = slotOf.get(id);
      if (slot === undefined) continue;
      hist[slot * NCELLS + cellRow + ((x / CELL) | 0)]++;
    }
  }

  /* --- largest connected component per kingdom, then a local centroid --- */
  for (let s = 0; s < live.length; s++) {
    const p = live[s];
    const base = s * NCELLS;

    /*
     * Flood fill over the cells this kingdom appears in, 4-connected. `seen` is
     * stamped rather than cleared so the whole thing stays allocation-free
     * across kingdoms and across sweeps.
     */
    stamp++;
    let blob = 0, bestSum = 0, bestCx = 0, bestCy = 0;

    for (let start = 0; start < NCELLS; start++) {
      if (hist[base + start] === 0 || seen[start] === stamp) continue;
      seen[start] = stamp;
      let top = 0;
      stack[top++] = start;
      // tile-weighted centre of this component, and its size
      let sum = 0, wx = 0, wy = 0;
      while (top > 0) {
        const c = stack[--top];
        const n = hist[base + c];
        const cx = c % CW, cy = (c / CW) | 0;
        sum += n;
        wx += (cx + 0.5) * n;
        wy += (cy + 0.5) * n;
        if (cx > 0 && hist[base + c - 1] > 0 && seen[c - 1] !== stamp) { seen[c - 1] = stamp; stack[top++] = c - 1; }
        if (cx < CW - 1 && hist[base + c + 1] > 0 && seen[c + 1] !== stamp) { seen[c + 1] = stamp; stack[top++] = c + 1; }
        if (cy > 0 && hist[base + c - CW] > 0 && seen[c - CW] !== stamp) { seen[c - CW] = stamp; stack[top++] = c - CW; }
        if (cy < CH - 1 && hist[base + c + CW] > 0 && seen[c + CW] !== stamp) { seen[c + CW] = stamp; stack[top++] = c + CW; }
      }
      if (sum > bestSum) { bestSum = sum; bestCx = wx / sum; bestCy = wy / sum; }
    }
    if (bestSum <= 0) continue;
    blob = bestSum;

    /*
     * The component's weighted centre can still land in a hole — a ring is one
     * component with nothing in the middle. So walk the component's own cells
     * for the one nearest that centre, which is by definition ground the realm
     * holds, and centre the label on the tiles around *it*.
     */
    stamp++;
    let anchorCell = -1, anchorDist = Infinity;
    for (let c = 0; c < NCELLS; c++) {
      if (hist[base + c] === 0) continue;
      const cx = c % CW + 0.5, cy = ((c / CW) | 0) + 0.5;
      const d = (cx - bestCx) * (cx - bestCx) + (cy - bestCy) * (cy - bestCy);
      if (d < anchorDist) { anchorDist = d; anchorCell = c; }
    }
    if (anchorCell < 0) continue;

    const acx = anchorCell % CW, acy = (anchorCell / CW) | 0;
    const x0 = Math.max(0, (acx - 1) * CELL), x1 = Math.min(MAP_W, (acx + 2) * CELL);
    const y0 = Math.max(0, (acy - 1) * CELL), y1 = Math.min(MAP_H, (acy + 2) * CELL);

    let sx = 0, sy = 0, n = 0;
    for (let y = y0; y < y1; y++) {
      const row = y * MAP_W;
      for (let x = x0; x < x1; x++) {
        if (owner[row + x] !== p.id) continue;
        sx += x; sy += y; n++;
      }
    }
    if (n === 0) continue;   // the chosen cell holds tiles by construction

    const tx = sx / n + 0.5, ty = sy / n + 0.5;
    const a = anchors.get(p.id);
    if (a) {
      a.tx = tx; a.ty = ty; a.blob = blob;
    } else {
      // a kingdom seen for the first time starts where it belongs rather than
      // sliding in from wherever the last one was
      anchors.set(p.id, { x: tx, y: ty, tx, ty, blob });
    }
  }

  /* --- forget the fallen --- */
  for (const id of [...anchors.keys()]) if (!slotOf.has(id)) anchors.delete(id);
}

/**
 * Slide every label toward its target.
 *
 * Called each frame. Exponential easing rather than a fixed step, so a border
 * that moves a little moves the name a little, and a kingdom that loses half
 * its land does not teleport its name across the map.
 */
export function stepAnchors(dt: number) {
  const k = 1 - Math.exp(-GLIDE_PER_SECOND * dt);
  for (const a of anchors.values()) {
    a.x += (a.tx - a.x) * k;
    a.y += (a.ty - a.y) * k;
  }
}
